import {
  afterNextRender,
  Component,
  computed,
  type ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { YouTubePlayer } from '@angular/youtube-player';

import { LightboxMap } from './map/lightbox-map';

@Component({
  imports: [LightboxMap, YouTubePlayer],
  selector: 'app-lightbox',
  styleUrl: './lightbox.css',
  templateUrl: './lightbox.html',
})
export class Lightbox {
  public readonly closed = output();
  public readonly closedAt = output<number>();
  public readonly hasMap = input(false);
  public readonly images = input<string[]>([]);
  public readonly listingId = input.required<string>();
  public readonly startIndex = input(0);
  public readonly youtubeId = input<string | undefined>();

  protected readonly dialogRef = viewChild<ElementRef<HTMLDialogElement>>('dialogRef');
  protected readonly galleryTab = viewChild<ElementRef<HTMLButtonElement>>('galleryTab');
  protected readonly mapTab = viewChild<ElementRef<HTMLButtonElement>>('mapTab');
  protected readonly tourTab = viewChild<ElementRef<HTMLButtonElement>>('tourTab');
  protected readonly tourWrapper = viewChild<ElementRef<HTMLDivElement>>('tourWrapper');

  protected readonly currentIndex = signal(0);
  protected readonly counter = computed(
    () => `${this.currentIndex() + 1} / ${this.images().length}`,
  );
  protected readonly currentImage = computed(() => {
    const _images = this.images();
    if (!_images.length) {
      return '';
    }
    return _images[this.currentIndex()];
  });
  protected readonly isOpen = signal(false);
  protected readonly mode = signal<'gallery' | 'map' | 'tour'>('gallery');
  protected readonly tourHeight = signal(480);
  protected readonly tourWidth = signal(854);

  private _activePointerId: null | number = null;
  private _returnFocusEl: HTMLElement | null = null;
  private _touchStartX = 0;
  private _touchStartY = 0;

  constructor() {
    afterNextRender(() => {
      this.updateVideoPlayerSize();
    });
  }

  protected close(): void {
    const _dialog = this.dialogRef()?.nativeElement;
    if (_dialog?.open) {
      _dialog.close();
    }
    this.isOpen.set(false);
    this.closed.emit();
    this.closedAt.emit(this.currentIndex());
    setTimeout(() => {
      this._returnFocusEl?.focus();
      this._returnFocusEl = null;
    });
  }

  protected next(): void {
    const _images = this.images();
    if (!_images.length) {
      return;
    }
    this.currentIndex.update((index) => (index + 1) % _images.length);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      this.next();
    } else if (event.key === 'ArrowLeft') {
      this.prev();
    }
  }

  protected onPointerCancel(event: PointerEvent): void {
    if (event.pointerId === this._activePointerId) {
      this._activePointerId = null;
    }
  }

  protected onPointerDown(event: PointerEvent): void {
    if (this._activePointerId !== null) {
      return;
    }
    // Skip edge touches so iOS/Android back/forward gesture still works
    if (event.clientX <= 20 || event.clientX >= window.innerWidth - 20) {
      return;
    }
    this._activePointerId = event.pointerId;
    this._touchStartX = event.clientX;
    this._touchStartY = event.clientY;
  }

  protected onPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this._activePointerId) {
      return;
    }

    this._activePointerId = null;
    const _dx = event.clientX - this._touchStartX;
    const _dy = event.clientY - this._touchStartY;

    if (Math.abs(_dx) >= 50 && Math.abs(_dx) > Math.abs(_dy)) {
      if (_dx < 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }

  protected onTourReady(event: YT.PlayerEvent): void {
    event.target.playVideo();
  }

  protected onTourStateChange(event: YT.OnStateChangeEvent): void {
    const _playerState: number = event.data;
    const _endedPlayerState: number = YT.PlayerState.ENDED;
    if (_playerState === _endedPlayerState) {
      event.target.stopVideo();
      this.switchToGallery();
    }
  }

  protected prev(): void {
    const _images = this.images();
    if (!_images.length) {
      return;
    }
    this.currentIndex.update((index) => (index - 1 + _images.length) % _images.length);
  }

  public show(startIndex = 0, mode: 'gallery' | 'map' | 'tour' = 'gallery'): void {
    this._returnFocusEl = document.activeElement as HTMLElement;
    this.mode.set(mode);
    this.currentIndex.set(Math.max(0, Math.min(startIndex, this.images().length - 1)));
    this.isOpen.set(true);

    setTimeout(() => {
      const _dialog = this.dialogRef()?.nativeElement;
      if (_dialog && !_dialog.open) {
        _dialog.closest = (selector: string) =>
          selector === 'el-dialog' ? null : Element.prototype.closest.call(_dialog, selector);
        _dialog.showModal();
      }

      const _tabElement =
        mode === 'tour'
          ? this.tourTab()?.nativeElement
          : mode === 'map'
            ? this.mapTab()?.nativeElement
            : this.galleryTab()?.nativeElement;
      _tabElement?.focus();

      if (mode === 'tour') {
        requestAnimationFrame(() => {
          this.updateVideoPlayerSize();
        });
      }
    });
  }

  protected switchToGallery(): void {
    this.mode.set('gallery');
  }

  protected switchToMap(): void {
    this.mode.set('map');
  }

  protected switchToTour(): void {
    this.mode.set('tour');
    requestAnimationFrame(() => {
      this.updateVideoPlayerSize();
    });
  }

  protected updateVideoPlayerSize(): void {
    const _wrapper = this.tourWrapper()?.nativeElement;
    if (!_wrapper) {
      return;
    }

    const _width = _wrapper.offsetWidth || window.innerWidth * 0.9;
    const _height = Math.floor((_width * 9) / 16);
    const _maxHeight = window.innerHeight * 0.8;

    if (_height > _maxHeight) {
      this.tourHeight.set(Math.floor(_maxHeight));
      this.tourWidth.set(Math.floor((_maxHeight * 16) / 9));
    } else {
      this.tourWidth.set(Math.floor(_width));
      this.tourHeight.set(_height);
    }
  }
}
